// Camada pura do formulário de dashboard_schema: converte o objeto salvo em
// um "draft" editável (ids locais, ordem preservada) e de volta. Sem React,
// sem MUI. _version não é escrito aqui — o backend é o dono dele.
//
// Objeto persistido:
//   { <aba>: { <slot>: ["nome exato do item no Zabbix", ...] }, _version: 3 }
//
// As abas vêm do catálogo (AbasSchema), passado como argumento a essas funções.

function tokenDe(valor) {
    return String(valor ?? "")
        .normalize("NFD")
        .replace(DIACRITICOS, "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
}

// sem catálogo (ou chave fora dele), devolve a própria chave
export function abaLabel(key, catalogo) {
    const token = tokenDe(key);
    const achada = (catalogo || []).find((aba) => tokenDe(aba.key) === token);
    return achada?.label || key;
}

const DIACRITICOS = new RegExp("[\\u0300-\\u036f]", "g"); // U+0300-U+036F

let seq = 0;
const uid = (prefixo) => `${prefixo}-${++seq}`;

function isPlainObject(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function toItemArray(value) {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined) return [];
    return [value];
}

export const emptyDraft = () => ({ abas: [], version: null, extras: {} });

export const novoSlot = (name = "") => ({
    id: uid("slot"),
    name,
    items: [],
    rawExtras: [],
});

// reinsere na posição original os valores não-string (rawExtras), pra não
// reordenar o array ao salvar
function reinserirExtras(itens, extras) {
    if (!extras || extras.length === 0) return itens;

    const saida = [...itens];
    for (const extra of [...extras].sort((a, b) => a.index - b.index)) {
        const posicao = Math.min(Math.max(extra.index, 0), saida.length);
        saida.splice(posicao, 0, extra.value);
    }
    return saida;
}

export const novaAba = (key = "") => ({
    id: uid("aba"),
    key,
    slots: [novoSlot()],
    extras: {},
});

// objeto salvo -> draft editável; tolera string dupla-codificada, string
// crua no nível 3 e valores fora da forma esperada (guardados em `extras`
// pra não perder dado)
export function normalizeSchema(raw) {
    let src = raw;
    if (typeof src === "string") {
        try {
            src = JSON.parse(src);
        } catch {
            return emptyDraft();
        }
    }
    if (!isPlainObject(src)) return emptyDraft();

    const draft = emptyDraft();
    draft.version = typeof src._version === "number" ? src._version : null;

    for (const abaKey of Object.keys(src)) {
        if (abaKey === "_version") continue;

        const abaValor = src[abaKey];
        if (!isPlainObject(abaValor)) {
            draft.extras[abaKey] = abaValor;
            continue;
        }

        const aba = { id: uid("aba"), key: abaKey, slots: [], extras: {} };

        for (const slotName of Object.keys(abaValor)) {
            const valor = abaValor[slotName];

            if (typeof valor !== "string" && !Array.isArray(valor)) {
                aba.extras[slotName] = valor;
                continue;
            }

            const items = [];
            const rawExtras = [];
            toItemArray(valor).forEach((item, index) => {
                if (typeof item === "string") items.push(item);
                else rawExtras.push({ index, value: item });
            });

            aba.slots.push({ id: uid("slot"), name: slotName, items, rawExtras });
        }

        draft.abas.push(aba);
    }

    return draft;
}

// draft -> objeto a persistir; poda slot sem nome/itens e aba sem slots
export function denormalizeSchema(draft) {
    if (!draft) return null;

    const saida = {};

    for (const aba of draft.abas || []) {
        const key = String(aba.key ?? "").trim();
        if (!key || key === "_version") continue;

        const corpo = {};

        for (const slot of aba.slots || []) {
            const name = String(slot.name ?? "").trim();
            if (!name) continue;

            const itens = (slot.items || [])
                .map((item) => String(item).replace(/\s+/g, " ").trim())
                .filter(Boolean);
            const todos = reinserirExtras(itens, slot.rawExtras);
            if (todos.length === 0) continue;

            // slots com nomes iguais: une em vez de sobrescrever
            corpo[name] = corpo[name]
                ? [...new Set([...corpo[name], ...todos])]
                : todos;
        }

        Object.assign(corpo, aba.extras || {});
        if (Object.keys(corpo).length === 0) continue;

        saida[key] = corpo;
    }

    Object.assign(saida, draft.extras || {});

    return Object.keys(saida).length === 0 ? null : saida;
}

// chave de comparação pro estado "dirty"; sensível à ordem, ignora ids/version
export function schemaDraftKey(draft) {
    return JSON.stringify([
        (draft?.abas || []).map((aba) => [
            aba.key,
            (aba.slots || []).map((slot) => [
                slot.name,
                slot.items || [],
                slot.rawExtras || [],
            ]),
            aba.extras || {},
        ]),
        draft?.extras || {},
    ]);
}

export function abaNoCatalogo(key, catalogo) {
    const token = tokenDe(key);
    return (catalogo || []).some((aba) => tokenDe(aba.key) === token);
}

export function abasDesconhecidasDoDraft(draft, catalogo) {
    return (draft?.abas || [])
        .map((aba) => String(aba.key ?? "").trim())
        .filter((key) => key && !abaNoCatalogo(key, catalogo));
}

export function abasDisponiveis(draft, catalogo) {
    const usadas = new Set((draft?.abas || []).map((aba) => tokenDe(aba.key)));
    return (catalogo || []).filter((aba) => !usadas.has(tokenDe(aba.key)));
}

export function contarSlotsDoDraft(draft) {
    const persistido = denormalizeSchema(draft);
    return contarSlotsDoSchema(persistido);
}

export function contarSlotsDoSchema(schema) {
    let src = schema;
    if (typeof src === "string") {
        try {
            src = JSON.parse(src);
        } catch {
            return 0;
        }
    }
    if (!isPlainObject(src)) return 0;

    let total = 0;
    for (const abaKey of Object.keys(src)) {
        if (abaKey === "_version") continue;
        const aba = src[abaKey];
        if (!isPlainObject(aba)) continue;
        total += Object.keys(aba).length;
    }
    return total;
}

export function hasSchema(modelo) {
    let src = modelo?.dashboard_schema;
    if (src == null) return false;
    if (typeof src === "string") {
        try {
            src = JSON.parse(src);
        } catch {
            return false;
        }
    }
    if (!isPlainObject(src)) return false;
    return Object.keys(src).some((key) => key !== "_version");
}
